import { memo, useMemo, useState } from 'react';
import { useGetOrders } from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import { Grid } from '@mantine/core';
import moment from 'moment';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState
} from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import { buildOrderFolio } from '../../utils/ordersUtils';
export default memo(function OrderCleanedByDateReport({
  date = '',
  close = () => {},
  storeId = ''
}: {
  date: string;
  close: () => void;
  storeId: string;
}) {
  const { t } = useTranslation(['FieldName']);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });

  const { data, isLoading, isError } = useGetOrders(!!(storeId && date), {
    cleanedDateTime: date,
    pageNum: pagination.pageIndex,
    storeId: storeId,
    limit: 9
  });

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows[0]?.totalRowsCount || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);
  const orders = useMemo(() => {
    return (
      data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
        list.push(...orders);
        return list;
      }, [] as IOrder[]) || []
    );
  }, [data]);

  const columns = useMemo<MRT_ColumnDef<IOrder>[]>(
    () => [
      {
        accessorFn: (row: IOrder) => buildOrderFolio(row?.folioPrefix, row?.sequence),
        header: t('FieldName:order')
      },
      {
        accessorFn: (row) => moment(row.cleanedDateTime).format('DD/MM/YYYY hh:mm'),
        header: t('FieldName:date')
      },
      {
        accessorFn: (row) => {
          const orderProducts = row?.products || [];
          const orderBuyAndGerProducts = row?.buyAndGetProducts || [];
          const allPieces = getAllPieces(orderProducts, orderBuyAndGerProducts);
          return allPieces;
        },
        header: t('FieldName:pieces')
      }
    ],
    []
  );
  const table = useMantineReactTable({
    columns,
    data: orders as IOrder[], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: 'Error cargando información'
        }
      : undefined,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: true,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualSorting: true,
    mantinePaginationProps: {
      showRowsPerPage: false
    },
    paginationDisplayMode: 'pages',
    state: { pagination, isLoading: isLoading }, //pass the pagination state to the table
    manualPagination: true,
    pageCount: pages,
    rowCount: totalRowsCount,
    onPaginationChange: setPagination,
    mantineTableProps: {
      highlightOnHover: false,
      striped: 'odd',
      withColumnBorders: true,
      withRowBorders: true,
      withTableBorder: true
    }
  });

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <MantineReactTable table={table} />
        </Grid.Col>
      </Grid>
    </>
  );
});
